import { FC, useState } from "react";
import { useAtomValue } from "jotai";

import { SQFTPerFloorEnum, TaskDriverStatusEnum } from "@/domain/models/common";

import { OptionType } from "../blocks";
import { useUpdateServiceMutation } from "../data/mutations/serviceMutations";
import { useUpdateTaskMutation } from "../data/mutations/taskMutations";
import { useServiceQuery } from "../data/queries/serviceQueries";
import { JobServiceTaskBaseType } from "../domain/task";
import { isDriverReviewingTaskAtom } from "../stores/atoms";
import { DriverScreenWithOptions } from "./DriverScreenWithOptions";

const SIZE_OPTIONS: OptionType<SQFTPerFloorEnum>[] = Object.values(
  SQFTPerFloorEnum,
).map(option => ({
  label: option,
  value: option,
}));

const DriverFillingSQFTPerFloor: FC<JobServiceTaskBaseType> = ({
  jobId,
  serviceId,
  taskId,
}) => {
  const { data: serviceData } = useServiceQuery({ jobId, serviceId });

  const [selectedOption, setSelectedOption] = useState<SQFTPerFloorEnum | null>(
    (serviceData?.floorSqft as SQFTPerFloorEnum) ?? null,
  );

  const isDriverReviewingTask = useAtomValue(isDriverReviewingTaskAtom);

  const {
    mutate: updateService,
    isPending: isUpdatingService,
    variables,
  } = useUpdateServiceMutation();
  const { mutate: updateTask, isPending: isUpdatingTask } =
    useUpdateTaskMutation();

  const onNextClick = () => {
    if (selectedOption) {
      updateService({
        jobId,
        serviceId,
        floorSqft: selectedOption,
        driverFlowStatus: isDriverReviewingTask
          ? TaskDriverStatusEnum.TASK_REVIEW
          : TaskDriverStatusEnum.TAKING_PHOTOS_AREAS_CLEANED,
      });

      updateTask({
        jobId,
        serviceId,
        taskId,
        driverFlowStatus: isDriverReviewingTask
          ? TaskDriverStatusEnum.TASK_REVIEW
          : TaskDriverStatusEnum.TAKING_PHOTOS_AREAS_CLEANED,
      });
    }
  };

  const isLoading = isUpdatingService || isUpdatingTask;

  const isPreviousButtonLoading =
    isLoading &&
    variables?.driverFlowStatus === TaskDriverStatusEnum.FILLING_FLOORS_CLEANED;

  const goToPreviousStep = () => {
    updateService({
      jobId,
      serviceId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_FLOORS_CLEANED,
    });

    updateTask({
      jobId,
      serviceId,
      taskId,
      driverFlowStatus: TaskDriverStatusEnum.FILLING_FLOORS_CLEANED,
    });
  };

  return (
    <DriverScreenWithOptions
      jobId={jobId}
      serviceId={serviceId}
      taskId={taskId}
      title="driver_filling_sqft_per_floor.title"
      optionsSelectorProps={{
        onSelect: setSelectedOption,
        value: selectedOption,
        options: SIZE_OPTIONS,
      }}
      bottomButtonProps={{
        nextButtonTitle: isDriverReviewingTask ? "review" : "next",
        isNextButtonLoading: isLoading,
        onNextButtonClick: onNextClick,
        isNextButtonDisabled: !selectedOption,
        previousButtonTitle: "driver_filling_sqft_per_floor.back_button",
        onPreviousButtonClick: isDriverReviewingTask
          ? undefined
          : () => goToPreviousStep(),
        isPreviousButtonLoading,
      }}
    />
  );
};

export { DriverFillingSQFTPerFloor };
